/**
 * utils.ts — tiny helpers shared by the adapted assistant-ui components.
 *
 * Replaces the two shadcn dependencies the upstream assistant-ui template
 * pulls in for these concerns:
 *   - `cn` (clsx + tailwind-merge) → `cx` (filter falsy, join with spaces). We
 *     have no Tailwind so there's nothing to merge.
 *   - a copy-to-clipboard hook used by the code-block header + action bars.
 *
 * Everything in `src/chat/aui/` is adapted from the upstream
 * `templates/minimal/components/assistant-ui/*` reference (assistant-ui repo):
 * the structure + working assistant-ui API usage are copied 1:1; only the
 * styling/dependency layer is swapped (shadcn → plain elements, lucide →
 * phosphor, Tailwind → our `style.css`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Join class names, dropping falsy values. Drop-in for shadcn's `cn` minus
 *  tailwind-merge (we have no conflicting Tailwind utilities to resolve). */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Copy text to the clipboard + track a transient "copied" flag (resets after
 * `copiedDuration` ms). Mirrors the `useCopyToClipboard` hook the upstream
 * markdown-text + action-bar components use.
 */
export function useCopyToClipboard(copiedDuration = 3000): {
  isCopied: boolean;
  copyToClipboard: (value: string) => void;
} {
  const [isCopied, setIsCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const copyToClipboard = useCallback(
    (value: string) => {
      if (!value || typeof navigator === 'undefined' || !navigator.clipboard) return;
      navigator.clipboard.writeText(value).then(
        () => {
          setIsCopied(true);
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => setIsCopied(false), copiedDuration);
        },
        () => {
          /* clipboard unavailable — no-op */
        },
      );
    },
    [copiedDuration],
  );

  return { isCopied, copyToClipboard };
}

/** Create + revoke an object URL for a File. Returns undefined when no file. */
export function useFileSrc(file: File | undefined): string | undefined {
  const [src, setSrc] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!file) {
      setSrc(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return src;
}
